import { useLocalStorage } from '@vueuse/core'

/**
 * Локаль vue-i18n: UI — localStorage; preferred_locale (SSO) — только для
 * нотификаций и начального выбора, если UI-предпочтения ещё нет.
 */
export const LOCALE_STORAGE_KEY = 'rtms-locale'
export const LOCALE_BY_USER_STORAGE_KEY = 'rtms-locale-by-user'

/**
 * Нормализует код локали до поддерживаемого значения.
 *
 * @param {*} v сырое значение из storage / UI
 * @returns {'en'|'ru'}
 */
export function normalizeAppLocale(v) {
  return v === 'en' || v === 'ru' ? v : 'ru'
}

/** Реактивная привязка к localStorage (можно импортировать для `watch` и т.п.). */
export const appLocaleStorage = useLocalStorage(LOCALE_STORAGE_KEY, 'ru', {
  serializer: {
    read: raw => {
      if (raw == null || raw === '') return 'ru'
      return normalizeAppLocale(typeof raw === 'string' ? raw : String(raw))
    },
    write: v => normalizeAppLocale(v),
  },
})

export const localeByUserStorage = useLocalStorage(LOCALE_BY_USER_STORAGE_KEY, {})

/**
 * Текущая сохранённая локаль приложения.
 *
 * @returns {'en'|'ru'}
 */
export function readStoredAppLocale() {
  return appLocaleStorage.value
}

/**
 * Записывает локаль в localStorage и возвращает нормализованный код.
 *
 * @param {*} code код локали
 * @returns {'en'|'ru'}
 */
export function persistAppLocale(code) {
  const n = normalizeAppLocale(code)
  appLocaleStorage.value = n
  return n
}

/**
 * Локаль UI для пользователя: явный выбор в localStorage, иначе preferred_locale, иначе ru.
 * Не перезаписывает UI из preferred_locale, если пользователь уже переключал язык в навбаре.
 *
 * @param {object|null|undefined} user текущий пользователь
 * @returns {'en'|'ru'}
 */
export function resolveLocaleForUser(user) {
  const userId = user?.id
  if (!userId) {
    return readStoredAppLocale()
  }

  const perUser = localeByUserStorage.value?.[String(userId)]
  if (perUser) {
    return persistAppLocale(perUser)
  }

  if (user?.preferred_locale != null && String(user.preferred_locale).trim() !== '') {
    return persistUserLocalePreference(userId, user.preferred_locale)
  }

  return persistAppLocale('ru')
}

/**
 * Явный выбор языка в переключателе — запоминается для пользователя.
 *
 * @param {number|string|null|undefined} userId id пользователя
 * @param {*} code код локали
 * @returns {'en'|'ru'}
 */
export function persistUserLocalePreference(userId, code) {
  const locale = persistAppLocale(code)
  if (userId != null) {
    localeByUserStorage.value = {
      ...localeByUserStorage.value,
      [String(userId)]: locale,
    }
  }
  return locale
}
